import SlotStateService from '../../cart-process/slot-state.service';
import { ProcessTypeEnum } from '../../models/cartDetails/process-type.enum';
import OtherProcessAuthService from './other-process-auth.service';
import { LoadersService } from '../../utils/loaders/loaders.service';
import { AppInjector } from '../../utils/services/set-app-injector';
import { MsisdnHelperService } from '../../utils/services/msisdn-helper.service';
import StockValidationService from '../../cart-process/stock-validation.service';
import MnpAuthService from './mnp-auth.service';
import { DataLayerService } from '../../customer-actions/data-layer.service';
import { CartDetailsSourceIdsEnum } from '../../models/cartDetails/data-source-ids.enum';
import { ICartDetails } from '../../interfaces/cart-details.interface';
import {
  NotificationProviderService,
  NotificationProviderTypeEnum
} from '../../external-interfaces/notification-provider.service';
import SlotModel from '../../models/slot.model';
import ValidationRulesService from '../../utils/services/validation-rules.service';

export default class AuthProcessSwitcherService {

  private slotStateService: SlotStateService = AppInjector.get(SlotStateService);
  private mnpAuthService: MnpAuthService = AppInjector.get(MnpAuthService);
  private otherProcessAuthService: OtherProcessAuthService = AppInjector.get(OtherProcessAuthService);
  private loadersService: LoadersService = AppInjector.get(LoadersService);
  private msisdnHelperService: MsisdnHelperService = AppInjector.get(MsisdnHelperService);
  private stockValidationService: StockValidationService = AppInjector.get(StockValidationService);
  private endpoint: any = AppInjector.get('Endpoint');
  private dataLayerService: DataLayerService = AppInjector.get(DataLayerService);
  private notificationProvider: NotificationProviderService = AppInjector.get(NotificationProviderService);
  private validationRules: ValidationRulesService = AppInjector.get(ValidationRulesService);
  private slot: SlotModel;
  private slotLoaderName: any;

  constructor(slot: SlotModel) {
    this.slot = slot;
    this.slotStateService.setOriginalState(this.slot);
    this.slot._computedFields.tempState = this.slotStateService.getTempState(this.slot.bundleNumber);
    this.slotLoaderName = this.loadersService.loaderActions.SLOT_LOADER + this.slot.slotNo;
  }

  public changeProcessToAcquisition(): void {
    this.loadersService.start(this.slotLoaderName);

    this.slot._computedFields.tempState.msisdn = '';
    this.dataLayerService.generateToken(CartDetailsSourceIdsEnum.CHANGE_PROCESS);
    this.dataLayerService.onChangeProcess();

    this.endpoint('newCart.changeProcess', {
      bundleNo: this.slot.bundleNumber,
      processType: ProcessTypeEnum.ACQUISITION.toString().toUpperCase()
    })
      .queue('processType')
      .run()
      .then((cartDetailsResponse: any) => {
        this.slotStateService.dropTempState(this.slot.bundleNumber);
        this.stockValidationService.invalidateStockForSlot(this.slot);
        this.propagateCartDetails(cartDetailsResponse, CartDetailsSourceIdsEnum.CHANGE_PROCESS);
      })
      .catch(() => {
        this.slotStateService.restoreSlotState(this.slot.bundleNumber);
      })
      .finally(() => {
        this.loadersService.stop(this.slotLoaderName);
      });
  }

  public changeProcessToMNP(resolve: Function, reject: Function): void {
    this.mnpAuthService.startProcess(
      this.slot.bundleNumber,
      this.msisdnHelperService.unformatMsisdn(this.slot._computedFields.tempState.newMnpNumberValue, true)
    ).then(() => {

    }).catch((errorMessage: string) => {
      if (errorMessage) {
        this.validationRules.addInputError('#tel-play-current-number-' + this.slot.ordinal, errorMessage);

        return;
      }

      if (reject) {
        reject();
      }
    });
  }

  public changeProcessToUpsell(inputProperties?: any): void {
    this.otherProcessAuthService.startProcess(
      this.slot.bundleNumber,
      this.msisdnHelperService.unformatMsisdn(this.slot._computedFields.tempState.upsellLoginNumberValue, true),
      ProcessTypeEnum.UPSELL
    )
      .catch((errorMessage: string) => {
        this.slotStateService.restoreSlotState(this.slot.bundleNumber);

        if (errorMessage) {
          this.validationRules.addInputError('tel-play-current-number-' + this.slot.ordinal, errorMessage);

          return;
        }
      })
      .finally(() => {
        if (inputProperties) {
          inputProperties.isEditState = this.slotStateService.isInProgress(this.slot._computedFields.tempState);
        }
      });
  }

  public changeProcessToRetention(inputProperties?: any): void {
    this.otherProcessAuthService.startProcess(
      this.slot.bundleNumber,
      this.msisdnHelperService.unformatMsisdn(this.slot._computedFields.tempState.newRetentionNumberValue, true),
      ProcessTypeEnum.RETENTION
    )
      .catch((errorMessage: string) => {
        this.slotStateService.restoreSlotState(this.slot.bundleNumber);

        if (errorMessage) {
          this.validationRules.addInputError('tel-play-retention-number-' + this.slot.ordinal, errorMessage);
          return;
        }
      })
      .finally(() => {
        if (inputProperties) {
          inputProperties.isEditState = this.slotStateService.isInProgress(this.slot._computedFields.tempState);
        }
      });
  }

  public changeProcessToPrepaid2Postpaid(inputProperties?: any): void {
    this.otherProcessAuthService.startProcess(
      this.slot.bundleNumber,
      this.msisdnHelperService.unformatMsisdn(this.slot._computedFields.tempState.newPrepaid2PostpaidNumberValue),
      ProcessTypeEnum.PREPAID_2_POSTPAID
    )
      .catch((errorMessage: string) => {
        this.slotStateService.restoreSlotState(this.slot.bundleNumber);

        if (errorMessage) {
          this.validationRules.addInputError('tel-play-migration-number-' + this.slot.ordinal, errorMessage);

          return;
        }
      })
      .finally(() => {
        if (inputProperties) {
          inputProperties.isEditState = this.slotStateService.isInProgress(this.slot._computedFields.tempState);
        }
      });
  }

  public enableMnpEdit(): void {
    this.slot._computedFields.tempState.MNP_EDIT_STATE = this.slot.processType !== ProcessTypeEnum.MNP || !this.slot.getMsisdn();
  }

  public enableRetentionEdit(): void {
    this.slot._computedFields.tempState.RETENTION_EDIT_STATE = this.slot.processType !== ProcessTypeEnum.RETENTION
      && this.slot.processType !== ProcessTypeEnum.PREPAID_2_POSTPAID || !this.slot.getMsisdn();
  }


  public clearEditStates(): void {
    const tempState = this.slot._computedFields.tempState;

    tempState.MNP_EDIT_STATE = false;
    tempState.RETENTION_EDIT_STATE = false;
    tempState.UPSELL_EDIT_STATE = false;
    tempState.PREPAID_2_POSTPAID_EDIT_STATE = false;
    tempState.newMnpNumberValue = '';
    tempState.newRetentionNumberValue = '';
    tempState.upsellLoginNumberValue = '';
    tempState.newPrepaid2PostpaidNumberValue = '';
  }

  public switchEditStates(): void {
    switch (this.slot._computedFields.tempState.tempProcessType) {
      case ProcessTypeEnum.ACQUISITION:
        this.changeProcessToAcquisition();
        break;
      case ProcessTypeEnum.MNP:
        this.enableMnpEdit();
        break;
      case ProcessTypeEnum.RETENTION:
        this.enableRetentionEdit();
        break;
    }
  }

  private propagateCartDetails(cartDetails: ICartDetails, additionalId?: CartDetailsSourceIdsEnum): void {
    if (additionalId) {
      cartDetails._sourceId = additionalId;
      cartDetails._sourceToken = this.dataLayerService.getToken(additionalId);
    }

    this.notificationProvider.publish(
      NotificationProviderTypeEnum.CART_DETAILS,
      cartDetails
    );
  }
}
